import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm";
import {
	CustomerOrderOfferEditApi,
	CustomerOrderOfferEditApi_FormInitData,
} from "src/api/generated/erp/sales/customerOrderOffer/api/customerOrderOfferEditApi.ts";
import { DeepPartial } from "react-hook-form";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import i18n from "i18next";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { dayJsToDateIsoString } from "src/utils/dayjsUtils.ts";
import dayjs from "dayjs";
import { findDefaultVatHandlingType, getDefaultVatCode } from "src/components/views/erp/sales/salesUtils.ts";
import { AavoMosaic } from "src/components/common/mosaic/AavoMosaic.tsx";
import { FormRichTextEditor } from "src/components/common/forms/fields/FormRichTextEditor.tsx";
import { useStoredState } from "../../../../../utils/useStoredState.ts";
import {
	CustomerOfferFormContent,
	CustomerOfferFormValues,
} from "src/components/views/erp/sales/customerOrderOffer/CustomerOfferFormContent.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import {
	getObjectAttributesEmbeddedFormDefaultValues,
	getObjectAttributesFromEmbeddedFormValues,
} from "src/components/views/erp/objectAttributes/ObjectAttributesEmbeddedForm.utils.tsx";
import { dropProps } from "src/utils/dropProps.ts";

export interface CustomerOrderOfferFormProps extends FormCommonProps<number> {
	customerOrderOfferId: number | undefined;
}

interface StoredDefaultValues extends Pick<CustomerOfferFormValues, "billingPlanEnabled"> {}

export const CustomerOrderOfferForm = ({
	customerOrderOfferId,
	onCompleted,
	onFormEdited,
}: CustomerOrderOfferFormProps) => {
	const { defaultSiteId, appUserId } = useGlobalInitData();
	const { tenantConfig } = useTenantCustomizations();
	const { customerOfferContractSigningEnabled } = tenantConfig.erp;

	const [storedDefaultValues, setStoredDefaultValues] = useStoredState<StoredDefaultValues>({
		key: "AEDE0CCBD1BA0103",
		defaultValue: {
			billingPlanEnabled: tenantConfig.erp.billingPlanEnabledByDefault,
		},
	});

	return (
		<AsyncForm<CustomerOrderOfferEditApi_FormInitData, CustomerOfferFormValues, number>
			fetch={() =>
				CustomerOrderOfferEditApi.getFormInitData({
					customerOrderOfferId,
				})
			}
			getDefaultValues={getDefaultValues}
			render={(params) => <FormContent {...params} />}
			submit={submitForm}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.customerOffer.form"),
				title: i18n.t("offer"),
				data: { customerOrderOfferId: customerOrderOfferId ?? null },
			}}
			noLayout
		/>
	);

	function getDefaultValues({
		customerOrderOffer,
		customer,
		currencyOptions,
		deliveryTermOptions,
		deliveryMethodOptions,
		vatHandlingTypeOptions,
		vatCodeOptions,
		salespersonOptions,
		customerPriceGroupOptions,
		attributeFields,
		attributeValues,
	}: CustomerOrderOfferEditApi_FormInitData): DeepPartial<CustomerOfferFormValues> {
		const attributeFormValues = getObjectAttributesEmbeddedFormDefaultValues(attributeValues, attributeFields);
		if (customerOrderOffer != null)
			return {
				...customerOrderOffer,
				updateDeliveryDateToLines: true,
				updateDeliveryDateToTasks: true,
				...attributeFormValues,
			};
		return {
			siteId: defaultSiteId,
			plannedDeliveryDate: dayJsToDateIsoString(dayjs()),
			responsiblePersonId: appUserId,
			salespersonId: salespersonOptions.find((sp) => sp.appUserId === appUserId)?.salespersonId,
			currencyId:
				customer?.currencyId ?? currencyOptions.find((currency) => currency.currencyCode === "EUR")?.currencyId,
			vatHandlingTypeId: findDefaultVatHandlingType(vatHandlingTypeOptions)?.vatHandlingTypeId,
			vatCodeId: getDefaultVatCode(vatCodeOptions).vatCodeId,
			inputPricesWithVat: tenantConfig.erp.inputPricesWithVatByDefault,
			paymentTermId: customer?.paymentTermId,
			customerPriceGroupId:
				customer?.customerPriceGroupId ??
				customerPriceGroupOptions.find((group) => group.isDefault)?.customerPriceGroupId,
			deliveryTermsId: deliveryTermOptions.find((term) => term.isDefault)?.deliveryTermsId,
			deliveryMethodId: deliveryMethodOptions[0]?.deliveryMethodId,
			billingPlanEnabled: storedDefaultValues.billingPlanEnabled,
			...attributeFormValues,
		};
	}

	async function submitForm(values: CustomerOfferFormValues) {
		setStoredDefaultValues({
			billingPlanEnabled: values.billingPlanEnabled,
		});
		const offerValues = customerOfferContractSigningEnabled ? dropProps(values, ["contractDate"]) : values;
		if (customerOrderOfferId != null) {
			await CustomerOrderOfferEditApi.update({
				request: dropProps(offerValues, ["attributes"]),
				attributes: getObjectAttributesFromEmbeddedFormValues(values),
			});
			return customerOrderOfferId;
		} else {
			return await CustomerOrderOfferEditApi.insert({
				offer: {
					...dropProps(offerValues, ["attributes"]),
					contractDate: customerOfferContractSigningEnabled ? null : values.contractDate,
				},
				attributes: getObjectAttributesFromEmbeddedFormValues(values),
			});
		}
	}
};

interface FormContentProps extends AsyncFormContentParams<CustomerOrderOfferEditApi_FormInitData, CustomerOfferFormValues> {}

const FormContent = ({
	control,
	data: { customerOrderOffer, customerAddresses, offerHasAnyLines, ...initData },
	...rest
}: FormContentProps) => {
	return (
		<AavoMosaic
			layout={{
				type: "tabs",
				items: [
					{
						type: "panel",
						title: i18n.t("offer"),
						content: (
							<CustomerOfferFormContent
								control={control}
								{...rest}
								customerOrderOffer={customerOrderOffer}
								initData={initData}
								customerAddresses={customerAddresses}
								offerHasAnyLines={offerHasAnyLines}
							/>
						),
					},
					{
						type: "panel",
						title: i18n.t("printout_start_text"),
						content: (
							<FormRichTextEditor
								control={control}
								name={"printoutStartText"}
								jasperReportsCompatibility
								sx={{
									flex: 1,
								}}
							/>
						),
					},
					{
						type: "panel",
						title: i18n.t("printout_end_text"),
						content: (
							<FormRichTextEditor
								control={control}
								name={"printoutEndText"}
								jasperReportsCompatibility
								sx={{ flex: 1 }}
							/>
						),
					},
				],
			}}
		/>
	);
};
